import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  RadioButtonUnchecked as UnselectedIcon,
  RadioButtonChecked as SelectedIcon,
  Clear as ClearIcon,
  SelectAll as SelectIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import useAppStore from '../store/useAppStore';

const WorkTypeSelector = () => {
  const { 
    workTypes, 
    activeWorkType, 
    quickInputMode,
    selectionMode,
    setActiveWorkType, 
    clearActiveWorkType,
    toggleSelectionMode 
  } = useAppStore();
  
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const handleWorkTypeClick = (workType) => {
    if (activeWorkType?.code === workType.code) {
      // If clicking the same type, toggle off
      clearActiveWorkType();
    } else {
      // Select new work type
      setActiveWorkType(workType);
    }
  };

  const handleClearClick = () => {
    // Special "empty" type for clearing cells
    const emptyType = {
      code: '',
      label: 'Puste',
      color: '#f5f5f5'
    };
    
    if (activeWorkType?.code === '') {
      clearActiveWorkType();
    } else {
      setActiveWorkType(emptyType);
    }
  };

  if (workTypes.length === 0) {
    return null;
  }

  // Definiowanie kolejności typów pracy
  const orderedTypes = ['B', 'Z', 'W', 'Del', 'N'];
  const sortedWorkTypes = orderedTypes
    .map(code => workTypes.find(wt => wt.code === code))
    .filter(Boolean);

  return (
    <Box sx={{ py: { xs: 0.25, sm: 0.5 } }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: 0.5, 
          fontWeight: 'bold', 
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          display: { xs: 'none', sm: 'block' }
        }}
      >
        Wybierz tryb pracy:
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        alignItems: 'center', 
        flexWrap: 'wrap',
        '@media (max-width: 600px)': {
          gap: 0.25,
        }
      }}>
        {sortedWorkTypes.map((workType) => {
          const isSelected = activeWorkType?.code === workType.code;
          
          return (
            <Tooltip 
              key={workType.code}
              title={`${workType.label} - kliknij aby ${isSelected ? 'wyłączyć' : 'wybrać'}`}
            >
              <Button
                variant={isSelected ? "contained" : "outlined"}
                startIcon={isSelected ? <SelectedIcon /> : <UnselectedIcon />}
                onClick={() => handleWorkTypeClick(workType)}
                sx={{
                  backgroundColor: isSelected ? workType.color : 'transparent',
                  borderColor: workType.color,
                  color: isSelected ? 'white' : workType.color,
                  fontWeight: isSelected ? 'bold' : 'normal',
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: { xs: 1, sm: 1.5 },
                  py: 0.5,
                  fontSize: { xs: '0.7rem', sm: '0.813rem' },
                  borderWidth: 2,
                  whiteSpace: 'nowrap',
                  '@media (max-width: 600px)': {
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      '& > svg': {
                        fontSize: '1rem'
                      }
                    }
                  },
                  '&:hover': {
                    backgroundColor: workType.color,
                    color: 'white',
                    opacity: 0.9,
                    borderWidth: 2
                  }
                }}
                size="small"
              >
                {`${workType.code} - ${workType.label}`}
              </Button>
            </Tooltip>
          );
        })}

        {/* Opcja "Puste" do usuwania wpisów */}
        <Tooltip title="Puste - usuwa wpisy z harmonogramu">
          <Button
            variant={activeWorkType?.code === '' ? "contained" : "outlined"}
            startIcon={activeWorkType?.code === '' ? <SelectedIcon /> : <ClearIcon />}
            onClick={handleClearClick}
            sx={{
              backgroundColor: activeWorkType?.code === '' ? '#f5f5f5' : 'transparent',
              borderColor: '#9e9e9e',
              color: activeWorkType?.code === '' ? '#333' : '#666',
              fontWeight: activeWorkType?.code === '' ? 'bold' : 'normal',
              textTransform: 'none',
              minWidth: 'auto',
              px: { xs: 1, sm: 1.5 },
              py: 0.5,
              fontSize: { xs: '0.7rem', sm: '0.813rem' },
              borderWidth: 2,
              whiteSpace: 'nowrap',
              '@media (max-width: 600px)': {
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                  '& > svg': {
                    fontSize: '1rem'
                  }
                }
              },
              '&:hover': {
                backgroundColor: '#f5f5f5',
                color: '#333',
                opacity: 0.9,
                borderWidth: 2
              }
            }}
            size="small"
          >
            Puste
          </Button>
        </Tooltip>

        {/* Przycisk przyciski zaznaczania */}
        <Tooltip title={selectionMode ? "Wyjdź z trybu zaznaczania" : "Włącz tryb zaznaczania komórek"}>
          <Button
            variant={selectionMode ? "contained" : "outlined"}
            startIcon={<SelectIcon />}
            onClick={toggleSelectionMode}
            sx={{
              backgroundColor: selectionMode ? 'primary.main' : 'transparent',
              borderColor: 'primary.main',
              color: selectionMode ? 'white' : 'primary.main',
              fontWeight: selectionMode ? 'bold' : 'normal',
              textTransform: 'none',
              minWidth: 'auto',
              px: { xs: 1, sm: 1.5 },
              py: 0.5,
              fontSize: { xs: '0.7rem', sm: '0.813rem' },
              borderWidth: 2,
              whiteSpace: 'nowrap',
              '@media (max-width: 600px)': {
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                  '& > svg': {
                    fontSize: '1rem'
                  }
                }
              },
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
                opacity: 0.9,
                borderWidth: 2
              }
            }}
            size="small"
          >
            {selectionMode ? 'Tryb edycji ON' : 'Tryb edycji'}
          </Button>
        </Tooltip>
        
        {/* Ikona pomocy z instrukcjami */}
        <Tooltip title="Instrukcje skrótów klawiszowych">
          <IconButton
            onClick={() => setHelpDialogOpen(true)}
            sx={{
              color: 'text.secondary',
              ml: 1
            }}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        
        {quickInputMode && (
          <Box sx={{ 
            ml: { xs: 0, sm: 2 }, 
            mt: { xs: 0.5, sm: 0 },
            display: { xs: 'none', sm: 'flex' }, 
            alignItems: 'center', 
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            flexBasis: { xs: '100%', sm: 'auto' }
          }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              Kliknij na komórki aby wpisać "{activeWorkType?.code === '' ? 'PUSTE' : activeWorkType?.code}"
            </Typography>
          </Box>
        )}
        
        {!quickInputMode && (
          <Box sx={{ 
            ml: { xs: 0, sm: 2 },
            mt: { xs: 0.5, sm: 0 },
            display: { xs: 'none', sm: 'block' },
            width: { xs: '100%', sm: 'auto' },
            flexBasis: { xs: '100%', sm: 'auto' }
          }}>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              Wybierz typ pracy aby włączyć szybkie wprowadzanie
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Dialog z instrukcjami skrótów klawiszowych */}
      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Skróty klawiszowe - Kalendarz Pracy
        </DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemText
                primary="Ctrl + C"
                secondary="Kopiuj zaznaczone komórki (wielokrotne wklejanie)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ctrl + X"
                secondary="Wytnij zaznaczone komórki (jednorazowe wklejanie)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ctrl + V"
                secondary="Wklej skopiowane lub wycięte komórki"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Delete"
                secondary="Usuń zawartość zaznaczonych komórek"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ctrl + Z"
                secondary="Cofnij ostatnią operację (zapisuje do bazy danych)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ctrl + Y"
                secondary="Ponów ostatnią cofniętą operację (zapisuje do bazy danych)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Escape"
                secondary="Wyłącz tryb zaznaczania"
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkTypeSelector;